import type {
  AudienceFilters,
  DemographicPanelId,
  PublishConfigTargetAudience,
  QuestionDemographicDimension,
} from '@brushfire/types'

/** Matches Target Audience wizard panel order. */
export const DEMOGRAPHIC_PANEL_ORDER: DemographicPanelId[] = [
  'ageRange',
  'senateDistrict',
  'location',
  'gender',
  'employment',
  'education',
  'maritalStatus',
  'deviceType',
  'numberOfChildren',
  'householdSize',
  'housingStatus',
  'industry',
  'registeredToVote',
  'currentVoterRegistration',
]

/** Targeting-only panels — not question-dashboard demographic dimensions. */
type TargetingOnlyPanelId = 'location' | 'senateDistrict' | 'registeredToVote' | 'currentVoterRegistration'

const PANEL_TO_DIMENSION: Record<
  Exclude<DemographicPanelId, TargetingOnlyPanelId>,
  QuestionDemographicDimension
> = {
  ageRange: 'age',
  gender: 'gender',
  education: 'education',
  employment: 'employment',
  deviceType: 'deviceType',
  maritalStatus: 'maritalStatus',
  householdSize: 'householdSize',
  numberOfChildren: 'numberOfChildren',
  industry: 'industry',
  housingStatus: 'housingStatus',
}

/**
 * BFP-536 — location panel may surface state (`region`), city, or both depending
 * on publish filters. When the panel is active but neither sub-field is set,
 * both dimensions are included.
 */
export function dimensionsFromLocationPanel(
  filters: AudienceFilters,
): QuestionDemographicDimension[] {
  const loc = filters.location
  const hasState = Boolean(loc?.state?.trim())
  const hasCity = Boolean(loc?.city?.trim())
  if (!hasState && !hasCity) {
    return ['region', 'city']
  }
  const out: QuestionDemographicDimension[] = []
  if (hasState) out.push('region')
  if (hasCity) out.push('city')
  return out
}

function dimensionsFromPanel(
  panelId: DemographicPanelId,
  filters: AudienceFilters,
): QuestionDemographicDimension[] {
  if (panelId === 'location') {
    return dimensionsFromLocationPanel(filters)
  }
  if (
    panelId === 'senateDistrict' ||
    panelId === 'registeredToVote' ||
    panelId === 'currentVoterRegistration'
  ) {
    return []
  }
  return [PANEL_TO_DIMENSION[panelId]]
}

/** True when a publish panel has a meaningful narrowing value (not an empty placeholder key). */
export function panelHasActiveAudienceFilter(
  panelId: DemographicPanelId,
  filters: AudienceFilters,
): boolean {
  switch (panelId) {
    case 'ageRange':
      return filters.ageRange?.min != null || filters.ageRange?.max != null
    case 'location':
      return Boolean(filters.location?.state?.trim() || filters.location?.city?.trim())
    case 'deviceType':
      return (filters.deviceType?.length ?? 0) > 0
    case 'senateDistrict':
      return Boolean(filters.senateDistrict?.trim())
    default: {
      const value = filters[panelId as keyof AudienceFilters]
      return Array.isArray(value) && value.length > 0
    }
  }
}

export function activeDemographicPanelIdsFromFilters(
  filters: AudienceFilters,
): DemographicPanelId[] {
  return DEMOGRAPHIC_PANEL_ORDER.filter((panelId) =>
    panelHasActiveAudienceFilter(panelId, filters),
  )
}

function readTargetAudience(publishConfig: unknown): PublishConfigTargetAudience | null {
  const targetAudience = (
    publishConfig as { targetAudience?: PublishConfigTargetAudience } | null
  )?.targetAudience
  return targetAudience ?? null
}

/**
 * Single source of truth for the active audience on a published instrument.
 * Reads `targetAudience.filters` only — never substitute AI suggestions (BFP-252).
 */
export function resolveActiveAudienceFiltersFromPublishConfig(
  publishConfig: unknown,
): AudienceFilters {
  const targetAudience = readTargetAudience(publishConfig)
  if (!targetAudience) return {}
  return targetAudience.filters ?? {}
}

/** Panel ids with active values on the instrument publish config. */
export function activeDemographicPanelIdsFromPublishConfig(
  publishConfig: unknown,
): DemographicPanelId[] {
  const filters = resolveActiveAudienceFiltersFromPublishConfig(publishConfig)
  return activeDemographicPanelIdsFromFilters(filters)
}

/** Drill-down / crosstab dimensions derived from publish audience panels. */
export function allowedDimensionsFromPublishConfig(
  publishConfig: unknown,
): QuestionDemographicDimension[] {
  const filters = resolveActiveAudienceFiltersFromPublishConfig(publishConfig)
  return activeDemographicPanelIdsFromFilters(filters).flatMap((panelId) =>
    dimensionsFromPanel(panelId, filters),
  )
}

/** @deprecated Use `dimensionsFromPanel` / `allowedDimensionsFromPublishConfig` for location. */
export const demographicPanelToDimension = {
  ...PANEL_TO_DIMENSION,
  location: 'region' as QuestionDemographicDimension,
} satisfies Record<
  Exclude<DemographicPanelId, 'senateDistrict' | 'registeredToVote' | 'currentVoterRegistration'>,
  QuestionDemographicDimension
>
